// /events/:eventId/tournament — Phases 1 and 2 of docs/plan-tournaments.md.
//
// ONE view, two modes off `canManage`, rather than two routes. A player and an
// organizer are looking at the same tournament and the same draw; the organizer
// additionally gets the roster and the seating editor. Splitting them would mean
// two copies of the draw renderer, which is most of the screen.
//
// The player's own seat is the top of the page and deliberately the largest
// thing on it. That is the whole point of the feature as it will actually be
// used: somebody is standing in a room wanting to know where to sit.
//
// Phase 2 added the draw: one button seats the whole round against the rounds
// already played. It sits ALONGSIDE the hand editor rather than replacing it,
// because the argument for drawing a chart in advance was that the organizer
// sees the seating before anybody is told and can fix it — which only holds if
// hand-editing still works. The draw also introduced byes, and with them the
// second meaning of "no seat": see `mySitOut` in `mySeatCard()`.
//
// Phase 3 added realtime. The view subscribes to `tournament.<eventId>` and
// re-fetches when a frame says something changed; the frame carries no seating
// and no viewer state, because one broadcast reaches everybody and `mySeat` is
// per-viewer by definition.
//
// ⚠ THE POLL STAYS, slowed from 20s to 60s, and that is deliberate rather than
// leftover. A missed frame in a live tournament is the expensive failure — a
// person standing in a room looking at the wrong table number — and the ways to
// miss one are real: the hub is in-process, so a second web box needs
// `WS_PEERS` set or half the room hears nothing at all, with nothing failing
// and health staying green. Realtime is the fast path; the poll is the floor.
//
// ⚠ AND THE ORGANIZER IS NOT AUTO-REFRESHED. Same data-loss reason the poll was
// player-only in Phase 1: re-rendering rebuilds the seating editor from the
// SAVED round and discards whatever draw they were assembling. A manager gets a
// line saying the tournament changed, and chooses when to reload.

import {
  ApiClientError,
  addTournamentPlayer,
  closeTournamentRound,
  completeTournament,
  createTournament,
  drawTournamentRound,
  fetchTournament,
  reopenTournamentRound,
  setTournamentScores,
  setTournamentScoring,
  setTournamentSeating,
  startTournamentRound,
  syncTournamentRoster,
  withdrawTournamentPlayer,
} from '../api/client.js';
import { navigate, onLeave, type View } from '../router.js';
import { subscribe } from '../realtime.js';
import { clientId } from '../api/clientId.js';
import { el } from '../dom.js';
import {
  tournamentChannelName,
  drawShape,
  seatLabel,
  tablesNeeded,
  type SeatAssignment,
  type SeatScore,
  type TournamentChangeReason,
  type TournamentDrawResult,
  type TournamentPlayer,
  type TournamentRoundRow,
  type TournamentStanding,
  type TournamentState,
} from '@msc/shared';

/**
 * The backstop poll while a round is live. Rounds change every ~20 minutes.
 *
 * Realtime does the real work now, so this is 60s rather than Phase 1's 20s --
 * it exists for the frame that never arrives, not for the ordinary case.
 */
const POLL_MS = 60000;

export const tournamentView: View = async (outlet, ctx) => {
  const eventId = Number(ctx.params.eventId);
  const main = el('main', { class: 'page' }, [el('p', { class: 'muted' }, 'Loading')]);
  outlet.append(main);

  if (!Number.isInteger(eventId) || eventId <= 0) {
    main.replaceChildren(el('p', { class: 'form-error' }, 'Invalid event.'));
    return;
  }

  let state: TournamentState | null = null;
  /**
   * What the last draw did, and which round it did it to.
   *
   * ⚠ Held HERE rather than written into the note element after the draw. The
   * draw's response triggers a full `render()`, so the element that was going
   * to carry the message is detached by the time the message exists -- writing
   * to it succeeds silently and shows nothing, which is the DOM-handle trap in
   * CLAUDE.md. The round number rides along so the note disappears by itself
   * once the organizer opens the next round rather than describing a stale draw.
   */
  let lastDraw: { roundNo: number; text: string } | null = null;
  /**
   * What a realtime frame said, when the viewer is an organizer.
   *
   * Managers are not auto-refreshed -- rebuilding the view would discard an
   * unsaved seating plan -- so the frame becomes a line of text and a button
   * instead. Cleared by the next successful load.
   */
  let remoteChange: string | null = null;
  let poll: number | undefined;

  // ⚠ Subscribed ONCE for the life of the view, not inside render(). The socket
  // is a module-level singleton that outlives every view, and subscriptions are
  // refcounted -- so subscribing per render would leak a handler on every
  // re-render and multiply the re-fetches by however many times the page had
  // been drawn.
  const unsubscribe = subscribe(tournamentChannelName(eventId), (frame) => {
    if (frame.event !== 'tournament.changed') return;
    // Our own write. The response to it has already refreshed this view, and
    // acting again would rebuild the seating editor from the saved round and
    // discard a plan still being assembled. Per TAB, so the same organizer's
    // board in another window still updates -- which is the point.
    if (frame.data.originClientId === clientId()) return;
    if (state?.canManage === true) {
      // Another organizer changed something. Say so; do not act.
      remoteChange = describeChange(frame.data.reason, frame.data.currentRound);
      render();
      return;
    }
    void load();
  });
  onLeave(() => {
    window.clearInterval(poll);
    unsubscribe();
  });

  async function load(): Promise<void> {
    try {
      state = await fetchTournament(eventId);
      // Whatever the frame was telling us about, we have just fetched it.
      remoteChange = null;
      render();
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 401) {
        navigate('/login', true);
        return;
      }
      // A 404 is not an error here: it means this event has no tournament yet,
      // which is the normal state of almost every event.
      if (err instanceof ApiClientError && err.status === 404) {
        state = null;
        renderAbsent();
        return;
      }
      main.replaceChildren(el('p', { class: 'form-error' }, 'Could not load the tournament.'));
    }
  }

  /** No tournament on this event. Organizers get a button; members get a line. */
  function renderAbsent(): void {
    const create = el('button', { class: 'btn', type: 'button' }, 'Start a tournament');
    const problem = el('p', { class: 'form-error', hidden: true });
    create.addEventListener('click', () => {
      create.setAttribute('disabled', 'disabled');
      void createTournament(eventId)
        .then((s) => {
          state = s;
          render();
        })
        .catch((err: unknown) => {
          problem.textContent =
            err instanceof ApiClientError ? err.message : 'Could not start it.';
          problem.hidden = false;
          create.removeAttribute('disabled');
        });
    });

    main.replaceChildren(
      backLink(eventId),
      el('div', { class: 'page-head' }, [el('h1', {}, 'Tournament')]),
      el(
        'p',
        { class: 'muted' },
        'No tournament has been set up for this event. An organizer can start one, ' +
          'seat players at tables, and move everybody on each round.',
      ),
      create,
      problem,
    );
  }

  function render(): void {
    const s = state;
    if (!s) return;

    window.clearInterval(poll);
    // ⚠ POLL FOR PLAYERS ONLY, and this is not an optimisation -- it is a
    // data-loss fix, found by being interrupted mid-click while testing.
    //
    // The organizer builds a seating plan in a `draft` held by the editor, and
    // `render()` rebuilds that editor from the SAVED round. So a poll that
    // re-renders every 20 seconds silently throws away however much of the draw
    // they had assembled but not yet saved -- and at 25 tables that is minutes
    // of work vanishing with no error and no explanation. Same family as the
    // CSS-transition trap in CLAUDE.md: a control that re-renders its own
    // container destroys whatever state lived in it.
    //
    // A player is waiting for someone else to change something, which is what a
    // poll is for. An organizer is the one making the change and has Refresh.
    if (s.status === 'running' && !s.canManage) {
      poll = window.setInterval(() => void load(), POLL_MS);
    }

    main.replaceChildren(
      backLink(eventId),
      el('div', { class: 'page-head' }, [
        el('h1', {}, s.eventTitle || 'Tournament'),
        el('p', { class: 'muted' }, statusLine(s)),
        refreshButton(),
      ]),
      ...(remoteChange === null ? [] : [remoteChangeCard(remoteChange)]),
      mySeatCard(s),
      ...(s.standings.length ? [standingsCard(s)] : []),
      ...(s.canManage ? [rosterCard(s), organizerControls(s)] : []),
      ...(s.canManage ? [seatingEditor(s), scoringCard(s)] : []),
      ...drawCards(s),
    );
  }

  /**
   * "Round 3 was drawn by somebody else." With a button, and nothing automatic.
   *
   * The whole point is that it does NOT reload on its own: this only ever
   * appears for a manager, and a manager may be halfway through a 25-table
   * seating plan that lives in the editor's memory.
   */
  function remoteChangeCard(text: string): HTMLElement {
    const reload = el('button', { class: 'btn', type: 'button' }, 'Reload');
    reload.addEventListener('click', () => void load());
    return el('section', { class: 'card' }, [
      el('h2', {}, 'Changed elsewhere'),
      el('p', {}, text),
      el(
        'p',
        { class: 'muted small' },
        'Nothing has been reloaded yet, so any seating you are part-way through ' +
          'is still here. Reload when you are ready.',
      ),
      reload,
    ]);
  }

  function refreshButton(): HTMLElement {
    const b = el('button', { class: 'btn-link', type: 'button' }, 'Refresh');
    b.addEventListener('click', () => void load());
    return b;
  }

  // --- The player's own seat ---------------------------------------------

  /**
   * "Round 3 · Table 7 · East", as large as the page allows.
   *
   * ⚠ The un-drawn case is the one that matters and it is easy to get wrong.
   * When the organizer opens the next round, `mySeat` legitimately goes null —
   * the new round has no seats yet and the previous seat is stale. Rendering
   * nothing there would blank every player's phone mid-tournament, which reads
   * as the app breaking at the exact moment it is being relied on. So an active
   * player with no seat in a running tournament is told the round is being
   * drawn, which is the truth.
   */
  function mySeatCard(s: TournamentState): HTMLElement {
    // ⚠ COMPLETE IS CHECKED FIRST, and it did not used to be. Until Phase 4 a
    // finished tournament had nothing to say, so `mySeat` winning was harmless.
    // Now it means the largest thing on the page is the table you sat at in the
    // last round of a tournament that is over -- stale information, rendered as
    // the answer to "where do I sit". A finished tournament says it is finished,
    // and who won.
    if (s.status === 'complete') {
      const winner =
        s.winnerPlayerId === null
          ? null
          : (s.standings.find((r) => r.playerId === s.winnerPlayerId)?.displayName ?? null);
      return el('section', { class: 'card seat-card seat-pending' }, [
        el('p', { class: 'seat-round' }, 'Finished'),
        el('p', { class: 'seat-table' }, winner === null ? 'No single winner' : winner),
        el(
          'p',
          { class: 'muted' },
          winner === null
            ? 'The standings are below.'
            : 'Won the tournament. The full standings are below.',
        ),
      ]);
    }

    const me = s.mySeat;
    if (me) {
      return el('section', { class: 'card seat-card' }, [
        el('p', { class: 'seat-round' }, `Round ${me.roundNo}`),
        el('p', { class: 'seat-table' }, `Table ${me.tableNo}`),
        el('p', { class: 'seat-wind' }, me.seatLabel),
      ]);
    }

    // ⚠ Sitting out is NOT "being drawn", and Phase 2 is what created the
    // difference. A byed player has no seat and never will this round, so the
    // Phase 1 screen would have left them watching "Being drawn" for a table
    // that is never coming -- for twenty minutes, in a room, while everyone
    // else plays. `mySitOut` is server-resolved precisely because the client
    // cannot tell the two nulls apart.
    if (s.mySitOut) {
      const round = s.rounds.find((r) => r.roundNo === s.currentRound);
      const others = (round?.byes ?? []).filter((b) => !s.players.some((p) => p.isMe && p.playerId === b.playerId));
      return el('section', { class: 'card seat-card seat-pending' }, [
        el('p', { class: 'seat-round' }, `Round ${s.currentRound}`),
        el('p', { class: 'seat-table' }, 'Sitting out'),
        el(
          'p',
          { class: 'muted' },
          others.length
            ? `The roster does not divide into full tables this round. Also sitting out: ${others
                .map((b) => b.displayName)
                .join(', ')}.`
            : 'The roster does not divide into full tables this round. You are back in for ' +
              'the next one. The draw sits out whoever has sat out least.',
        ),
      ]);
    }

    // Is the viewer even playing? An organizer who is not on the roster should
    // not be told a round is being drawn for them.
    const playing = s.players.some((p) => p.status === 'active' && p.userId !== null && p.isMe);
    if (s.status === 'running' && playing) {
      return el('section', { class: 'card seat-card seat-pending' }, [
        el('p', { class: 'seat-round' }, `Round ${s.currentRound}`),
        el('p', { class: 'seat-table' }, 'Being drawn'),
        el('p', { class: 'muted' }, 'You will be told your table as soon as it is set.'),
      ]);
    }
    return el('section', { class: 'card' }, [
      el('h2', {}, 'Not seated'),
      el(
        'p',
        { class: 'muted' },
        s.status === 'setup'
          ? 'The tournament has not started yet.'
          : 'You are not seated in this round.',
      ),
    ]);
  }

  // --- Organizer: roster --------------------------------------------------

  function rosterCard(s: TournamentState): HTMLElement {
    const problem = el('p', { class: 'form-error', hidden: true });
    const act = (p: Promise<TournamentState>, btn: HTMLElement) => {
      btn.setAttribute('disabled', 'disabled');
      problem.hidden = true;
      void p
        .then((next) => {
          state = next;
          render();
        })
        .catch((err: unknown) => {
          problem.textContent =
            err instanceof ApiClientError ? err.message : 'That did not work.';
          problem.hidden = false;
          btn.removeAttribute('disabled');
        });
    };

    const sync = el('button', { class: 'btn', type: 'button' }, 'Add everyone checked in');
    sync.addEventListener('click', () => act(syncTournamentRoster(eventId), sync));

    const nameBox = el('input', {
      type: 'text',
      placeholder: 'Name',
      'aria-label': 'Walk-in name',
      maxlength: '120',
    }) as HTMLInputElement;
    const add = el('button', { class: 'btn', type: 'button' }, 'Add walk-in');

    /**
     * Add the typed walk-in, then leave the cursor where it is.
     *
     * ⚠ Focus is restored deliberately. Adding a room of walk-ins is the most
     * type-heavy thing an organizer ever does in this app, and it is done
     * standing up with people waiting -- so every name that costs a mouse trip
     * back to the box is a name typed slower than the queue in front of them.
     */
    const addWalkIn = (): void => {
      const displayName = nameBox.value.trim();
      if (!displayName) {
        nameBox.focus();
        return;
      }
      nameBox.value = '';
      act(addTournamentPlayer(eventId, { displayName }), add);
      nameBox.focus();
    };

    add.addEventListener('click', addWalkIn);

    // ⚠ Return submits. The box is NOT in a <form> -- this card is a set of
    // buttons rather than a form -- so nothing gives it Enter for free, and
    // pressing it did literally nothing: the name just sat there. A keydown
    // listener is the whole fix.
    nameBox.addEventListener('keydown', (ev) => {
      if (ev.key !== 'Enter') return;
      // Stops a stray Enter from reaching anything that might treat it as a
      // default action, and from inserting a newline into the value.
      ev.preventDefault();
      addWalkIn();
    });

    const active = s.players.filter((p) => p.status === 'active');
    const withdrawn = s.players.filter((p) => p.status === 'withdrawn');

    return el('section', { class: 'card' }, [
      el('h2', {}, `Roster: ${active.length} playing`),
      el(
        'p',
        { class: 'muted small' },
        // Only the checked-in are pulled in, and saying so prevents the obvious
        // support question: "why isn't everyone who said yes on here?"
        'Only people who have confirmed on the day are added. An advance yes who ' +
          'never turned up should not be given a seat. Tap again as latecomers arrive.',
      ),
      el('p', {}, [sync]),
      // ⚠ THE DRAW'S PHRASING, not the hand editor's. These two genuinely
      // disagree -- at 5 players the draw makes 1 table and sits somebody out,
      // while hand-seating really does offer a second table with one player in
      // it -- so this is not a copy nit, it is two different rooms from one
      // roster. The headline follows the DRAW because that is what the button
      // on this card does, and a mahjong table with one player is not a game.
      // The hand editor keeps `tablePlanLine()`, which is correct where it is.
      el(
        'p',
        { class: 'muted small' },
        drawPlanLine(drawShape(active.length, s.seatsPerTable), s.seatsPerTable),
      ),
      el('ul', { class: 'card-list' }, active.map((p) => playerRow(p, act))),
      ...(withdrawn.length
        ? [
            el('p', { class: 'muted small' }, `Withdrawn: ${withdrawn
              .map((p) => p.displayName)
              .join(', ')}`),
          ]
        : []),
      el('div', { class: 'walkin-row' }, [nameBox, add]),
      el(
        'p',
        { class: 'muted small' },
        'A walk-in with no account can be seated but cannot be sent alerts.',
      ),
      problem,
    ]);
  }

  function playerRow(
    p: TournamentPlayer,
    act: (promise: Promise<TournamentState>, btn: HTMLElement) => void,
  ): HTMLElement {
    const drop = el('button', { class: 'btn-link btn-danger', type: 'button' }, 'Withdraw');
    drop.addEventListener('click', () => {
      if (!window.confirm(`Withdraw ${p.displayName}? They keep their place in rounds already played.`)) {
        return;
      }
      act(withdrawTournamentPlayer(eventId, p.playerId), drop);
    });
    return el('li', { class: 'roster-row' }, [
      el('span', {}, p.displayName),
      ...(p.canBeNotified ? [] : [el('span', { class: 'badge' }, 'no account')]),
      drop,
    ]);
  }

  // --- Organizer: rounds -------------------------------------------------

  function organizerControls(s: TournamentState): HTMLElement {
    const problem = el('p', { class: 'form-error', hidden: true });
    const run = (p: Promise<TournamentState>, btn: HTMLElement) => {
      btn.setAttribute('disabled', 'disabled');
      void p
        .then((next) => {
          state = next;
          render();
        })
        .catch((err: unknown) => {
          problem.textContent = err instanceof ApiClientError ? err.message : 'That did not work.';
          problem.hidden = false;
          btn.removeAttribute('disabled');
        });
    };

    const next = el(
      'button',
      { class: 'btn', type: 'button' },
      s.currentRound === 0 ? 'Start round 1' : `Start round ${s.currentRound + 1}`,
    );
    next.addEventListener('click', () => {
      // Worth a prompt: opening the next round blanks every player's seat until
      // the new one is drawn, which they WILL notice.
      if (
        s.currentRound > 0 &&
        !window.confirm(
          `Start round ${s.currentRound + 1}? Every player's table disappears until ` +
            'you seat the new round.',
        )
      ) {
        return;
      }
      run(startTournamentRound(eventId), next);
    });

    const finish = el('button', { class: 'btn btn-danger', type: 'button' }, 'End tournament');
    finish.addEventListener('click', () => {
      // ⚠ Ending is the one irreversible act here -- rounds cannot be reopened
      // afterwards -- so the confirmation says what it settles rather than just
      // asking twice. The winner is READ from the standings; this button never
      // names one.
      const leader = s.standings.find((r) => !r.withdrawn && r.roundsPlayed > 0);
      const tied =
        leader !== undefined &&
        s.standings.filter((r) => !r.withdrawn && r.roundsPlayed > 0 && r.total === leader.total)
            .length > 1;
      const question = leader === undefined
        ? 'End this tournament? Nothing has been scored, so no winner will be recorded.'
        : tied
          ? 'End this tournament? The top of the standings is TIED, so no single winner ' +
            'will be recorded. Settle it in the room first if you want one.'
          : `End this tournament? ${leader.displayName} wins on ${formatScore(leader.total)}, ` +
            'and no round can be reopened afterwards.';
      if (!window.confirm(question)) return;
      run(completeTournament(eventId), finish);
    });

    return el('section', { class: 'card' }, [
      el('h2', {}, 'Rounds'),
      el('div', { class: 'walkin-row' }, [
        ...(s.status === 'complete' ? [] : [next]),
        ...(s.status === 'complete' ? [] : [finish]),
      ]),
      // Offered to the organizer only, because they are the one with a laptop
      // and a screen. Any member may open it -- it reads through the same
      // endpoint -- but nobody else has a reason to.
      el('p', {}, [
        el('a', { href: `/events/${eventId}/tournament/board` }, 'Open the room display'),
      ]),
      el(
        'p',
        { class: 'muted small' },
        'A big screen showing the whole draw, for the players with no account. ' +
          'It updates itself as you seat each round.',
      ),
      problem,
    ]);
  }

  // --- Standings, for everybody ------------------------------------------

  /**
   * Who is winning. Shown to every viewer, not just organizers.
   *
   * Standings in a tournament room are public by definition -- they go on the
   * projector -- so hiding them from players would be inventing a secret the
   * room does not keep.
   *
   * ⚠ `roundsPlayed` is on every row on purpose, and it is not padding. A bye
   * scores nothing, which is neutral under a zero-sum ruleset (riichi's
   * 100,000 average out) and a REAL disadvantage without one, where everybody
   * else banked points that round. The app does not silently compensate for
   * that; it shows the number so the comparison is visible.
   */
  function standingsCard(s: TournamentState): HTMLElement {
    const winner = s.winnerPlayerId;
    const rows = s.standings.map((row) => standingRow(row, s, winner));

    return el('section', { class: 'card' }, [
      el('h2', {}, s.status === 'complete' ? 'Final standings' : 'Standings'),
      ...(s.status === 'complete' ? [winnerLine(s)] : []),
      ...(s.canManage && s.status === 'complete' && s.tiedForFirst.length > 0
        ? [tiebreakRow(s)]
        : []),
      el('ol', { class: 'standings' }, rows),
      ...(s.standings.some((r) => r.roundsPlayed !== s.standings[0]?.roundsPlayed)
        ? [
            el(
              'p',
              { class: 'muted small' },
              'Rounds played differ, because the roster does not divide evenly and ' +
                'somebody sits each round out. A round sat out scores nothing.',
            ),
          ]
        : []),
    ]);
  }

  /**
   * Record who won the play-off, when the tournament ended tied.
   *
   * ⚠ THIS EXISTS BECAUSE THE SERVER ALREADY ACCEPTED IT AND NOTHING ON SCREEN
   * COULD REACH IT. The complete endpoint takes a `winnerPlayerId` to break a
   * tie, and it was built and tested that way -- but with no control here, a
   * tied tournament was permanently winnerless in the browser, which is exactly
   * the outcome the tiebreak was added to avoid. Caught by ending a tied
   * tournament in a browser rather than by any assertion: the API tests passed
   * because they call the endpoint directly.
   *
   * Only the tied players are offered, because only they can be named -- the
   * server refuses anybody else, and offering a choice the server will reject is
   * a worse control than offering none.
   */
  function tiebreakRow(s: TournamentState): HTMLElement {
    const problem = el('p', { class: 'form-error', hidden: true });
    const buttons = s.tiedForFirst.map((playerId) => {
      const name = s.standings.find((r) => r.playerId === playerId)?.displayName ?? 'Player';
      const b = el('button', { class: 'btn', type: 'button' }, `${name} won`);
      b.addEventListener('click', () => {
        if (!window.confirm(`Record ${name} as the winner of the play-off?`)) return;
        b.setAttribute('disabled', 'disabled');
        problem.hidden = true;
        void completeTournament(eventId, { winnerPlayerId: playerId })
          .then((next) => {
            state = next;
            render();
          })
          .catch((err: unknown) => {
            problem.textContent =
              err instanceof ApiClientError ? err.message : 'Could not record the winner.';
            problem.hidden = false;
            b.removeAttribute('disabled');
          });
      });
      return b;
    });

    return el('div', {}, [
      el(
        'p',
        { class: 'muted small' },
        'Settle it in the room (a play-off, or whatever your group does), then say ' +
          'who won. Nothing else about the tournament can change.',
      ),
      el('div', { class: 'walkin-row' }, buttons),
      problem,
    ]);
  }

  function standingRow(
    row: TournamentStanding,
    s: TournamentState,
    winnerPlayerId: number | null,
  ): HTMLElement {
    const isMe = s.players.some((p) => p.isMe && p.playerId === row.playerId);
    const classes = ['standing-row'];
    if (isMe) classes.push('standing-me');
    if (row.playerId === winnerPlayerId) classes.push('standing-winner');
    if (row.withdrawn) classes.push('standing-withdrawn');

    return el('li', { class: classes.join(' ') }, [
      // The rank is SHARED on a tie -- two players on the same total are both
      // 3rd and the next is 5th -- because "who is second" has to be answerable
      // out loud in a room.
      el('span', { class: 'standing-rank' }, String(row.rank)),
      el('span', { class: 'standing-name' }, row.displayName),
      ...(row.withdrawn ? [el('span', { class: 'badge' }, 'withdrawn')] : []),
      el('span', { class: 'standing-total' }, formatScore(row.total)),
      el(
        'span',
        { class: 'standing-rounds muted small' },
        `${row.roundsPlayed} ${row.roundsPlayed === 1 ? 'round' : 'rounds'}`,
      ),
    ]);
  }

  /** Who won, or the honest admission that nobody was crowned. */
  function winnerLine(s: TournamentState): HTMLElement {
    const name = (id: number) =>
      s.standings.find((r) => r.playerId === id)?.displayName ?? `Player ${id}`;

    if (s.winnerPlayerId !== null) {
      return el('p', { class: 'winner-line' }, `Winner: ${name(s.winnerPlayerId)}`);
    }
    if (s.tiedForFirst.length > 0) {
      // ⚠ Said plainly rather than resolved. A tie at the top is a real outcome
      // that a room settles with a play-off or a tiebreak rule the app knows
      // nothing about, and picking the alphabetically-first player would be a
      // silently invented result.
      return el(
        'p',
        { class: 'winner-line' },
        `Tied for first: ${s.tiedForFirst.map(name).join(' and ')}, no single winner.`,
      );
    }
    return el(
      'p',
      { class: 'muted' },
      'No winner was recorded, because no round was scored.',
    );
  }

  // --- Organizer: the seating editor -------------------------------------

  /**
   * Build the current round's seating and save it in one go.
   *
   * A `<select>` per seat rather than drag and drop, deliberately: this is used
   * on a phone, standing up, in a room. The plan is held in memory and PUT
   * wholesale, matching the endpoint — which replaces the round rather than
   * diffing it, so a half-applied re-seat is impossible.
   */
  function seatingEditor(s: TournamentState): HTMLElement {
    if (s.status !== 'running' || s.currentRound === 0) {
      return el('section', { class: 'card' }, [
        el('h2', {}, 'Seating'),
        el('p', { class: 'muted' }, 'Start a round to seat players.'),
      ]);
    }

    const active = s.players.filter((p) => p.status === 'active');
    const round = s.rounds.find((r) => r.roundNo === s.currentRound);
    const plan = tablesNeeded(active.length, s.seatsPerTable);

    // Seed from what is already saved, so opening the editor mid-seating shows
    // the room as it stands rather than a blank grid.
    const draft = new Map<string, number>();
    for (const t of round?.tables ?? []) {
      for (const seat of t.seats) draft.set(`${t.tableNo}:${seat.seatNo}`, seat.playerId);
    }
    // ⚠ A DRAWN round owns its own table count. Taking the max with
    // `tablesNeeded()` was right in Phase 1, when the only way to seat a round
    // was to fill empty tables by hand -- but after a draw it leaves a spare
    // empty table on screen (13 players draw 3 tables of four and sit one out,
    // while tablesNeeded() wants 4), which reads as the draw having forgotten a
    // table. An organizer who really wants another has "+ Add a table".
    let tableCount = round?.tables.length
      ? round.tables.length
      : Math.max(plan.tables, 1);

    const host = el('div', {});
    const problem = el('p', { class: 'form-error', hidden: true });

    function seatedElsewhere(playerId: number, key: string): boolean {
      for (const [k, v] of draft) if (v === playerId && k !== key) return true;
      return false;
    }

    function drawEditor(): void {
      const tables: HTMLElement[] = [];
      for (let t = 1; t <= tableCount; t += 1) {
        const seats: HTMLElement[] = [];
        for (let n = 1; n <= s.seatsPerTable; n += 1) {
          const key = `${t}:${n}`;
          const chosen = draft.get(key);
          const pick = el('select', {
            class: 'seat-pick',
            'aria-label': `Table ${t}, ${seatLabel(n, s.seatsPerTable)}`,
          }) as HTMLSelectElement;
          pick.append(el('option', { value: '' }, '(empty)'));
          for (const p of active) {
            // Hide anyone already placed at another seat, so the control cannot
            // build a plan the server will reject.
            if (seatedElsewhere(p.playerId, key)) continue;
            const opt = el('option', { value: String(p.playerId) }, p.displayName) as HTMLOptionElement;
            if (chosen === p.playerId) opt.selected = true;
            pick.append(opt);
          }
          pick.addEventListener('change', () => {
            const v = Number(pick.value);
            if (v) draft.set(key, v);
            else draft.delete(key);
            drawEditor();
          });
          seats.push(
            el('div', { class: 'seat-slot' }, [
              el('span', { class: 'muted small' }, seatLabel(n, s.seatsPerTable)),
              pick,
            ]),
          );
        }
        tables.push(
          el('div', { class: 'seat-table-card' }, [el('h3', {}, `Table ${t}`), ...seats]),
        );
      }
      host.replaceChildren(...tables);
    }
    drawEditor();

    const addTable = el('button', { class: 'btn-link', type: 'button' }, '+ Add a table');
    addTable.addEventListener('click', () => {
      tableCount += 1;
      drawEditor();
    });

    // The Phase 2 draw. Separate from the hand editor above rather than
    // replacing it: the plan's argument for a pre-drawn chart was that the
    // organizer sees the draw BEFORE anyone is told and can fix it, and that
    // only holds if hand-editing survives.
    const shape = drawShape(active.length, s.seatsPerTable);
    const seated = round?.tables.some((t) => t.seats.length > 0) === true;
    const draw = el(
      'button',
      { class: 'btn', type: 'button' },
      seated ? `Re-draw round ${s.currentRound}` : `Draw round ${s.currentRound}`,
    );
    const note = lastDraw?.roundNo === s.currentRound ? lastDraw.text : '';
    const drawNote = el('p', { class: 'muted small' }, note);
    drawNote.hidden = note === '';
    draw.addEventListener('click', () => {
      // Re-drawing moves people who have already been told where to sit, which
      // is worth one question. Drawing an unseated round moves nobody.
      if (
        seated &&
        !window.confirm(
          `Re-draw round ${s.currentRound}? Everyone already seated will be moved.`,
        )
      ) {
        return;
      }
      draw.setAttribute('disabled', 'disabled');
      problem.hidden = true;
      void drawTournamentRound(eventId, s.currentRound)
        .then((result) => {
          // The count is the honest part: repeat avoidance is best-effort, so
          // the organizer is handed the number and can re-draw, rather than
          // being told the draw is perfect.
          lastDraw = { roundNo: s.currentRound, text: drawResultLine(result) };
          state = result.state;
          render();
        })
        .catch((err: unknown) => {
          problem.textContent =
            err instanceof ApiClientError ? err.message : 'Could not draw the round.';
          problem.hidden = false;
          draw.removeAttribute('disabled');
        });
    });

    // NOT the Phase 2 draw. It fills in the order the roster is listed and
    // knows nothing about standings or who has already played whom — naming it
    // plainly so it is never mistaken for the real thing.
    const fill = el('button', { class: 'btn-link', type: 'button' }, 'Fill in listed order');
    fill.addEventListener('click', () => {
      draft.clear();
      tableCount = Math.max(plan.tables, 1);
      active.forEach((p, i) => {
        draft.set(`${Math.floor(i / s.seatsPerTable) + 1}:${(i % s.seatsPerTable) + 1}`, p.playerId);
      });
      drawEditor();
    });

    const save = el('button', { class: 'btn', type: 'button' }, 'Save seating');
    save.addEventListener('click', () => {
      const seats: SeatAssignment[] = [...draft.entries()].map(([key, playerId]) => {
        const [tableNo, seatNo] = key.split(':').map(Number);
        return { playerId, tableNo: tableNo ?? 1, seatNo: seatNo ?? 1 };
      });
      save.setAttribute('disabled', 'disabled');
      problem.hidden = true;
      void setTournamentSeating(eventId, s.currentRound, { seats })
        .then((next) => {
          state = next;
          render();
        })
        .catch((err: unknown) => {
          // The server's message names the player, so show it verbatim.
          problem.textContent =
            err instanceof ApiClientError ? err.message : 'Could not save the seating.';
          problem.hidden = false;
          save.removeAttribute('disabled');
        });
    });

    return el('section', { class: 'card' }, [
      el('h2', {}, `Seat round ${s.currentRound}`),
      el('div', { class: 'walkin-row' }, [draw]),
      el('p', { class: 'muted small' }, drawPlanLine(shape, s.seatsPerTable)),
      drawNote,
      el('h3', {}, 'Or seat them by hand'),
      el('p', { class: 'muted small' }, tablePlanLine(plan, s.seatsPerTable)),
      el('div', { class: 'walkin-row' }, [fill, addTable]),
      host,
      el('div', { class: 'walkin-row' }, [save]),
      problem,
    ]);
  }

  // --- Organizer: entering the scores ------------------------------------

  /**
   * Score the current round, one table at a time.
   *
   * ⚠ THE SAVE IS PARTIAL, and the whole panel is built around that. An
   * organizer walks a room of 25 tables typing numbers for twenty minutes, so
   * holding it all in a browser tab until the end is the Phase 1 data-loss trap
   * again -- one bad refresh and the afternoon is gone. Each table saves on its
   * own, the server updates those seats and leaves the rest alone, and the
   * round is only checked for completeness when it is CLOSED.
   *
   * The running total under each table is the reason the whole variant question
   * turned out not to matter: with `tableScoreTotal` set it says OK or names the
   * difference, and with it null it just shows the sum.
   */
  function scoringCard(s: TournamentState): HTMLElement {
    if (s.currentRound === 0) {
      return el('section', { class: 'card' }, [
        el('h2', {}, 'Scores'),
        el('p', { class: 'muted' }, 'Start and seat a round to score it.'),
      ]);
    }
    const round = s.rounds.find((r) => r.roundNo === s.currentRound);
    const tables = round?.tables ?? [];
    const closed = round?.status === 'closed';
    const problem = el('p', { class: 'form-error', hidden: true });

    if (!tables.length) {
      return el('section', { class: 'card' }, [
        el('h2', {}, `Score round ${s.currentRound}`),
        el('p', { class: 'muted' }, 'Seat the round first. There is nothing to score yet.'),
      ]);
    }

    const act = (p: Promise<TournamentState>, btn: HTMLElement) => {
      btn.setAttribute('disabled', 'disabled');
      problem.hidden = true;
      void p
        .then((next) => {
          state = next;
          render();
        })
        .catch((err: unknown) => {
          // The server's message names the player or the table and says by how
          // much a total is off, so it is shown verbatim.
          problem.textContent =
            err instanceof ApiClientError ? err.message : 'Could not save the scores.';
          problem.hidden = false;
          btn.removeAttribute('disabled');
        });
    };

    return el('section', { class: 'card' }, [
      el('h2', {}, `${closed ? 'Scores for' : 'Score'} round ${s.currentRound}`),
      scoringSettingRow(s, act),
      ...(closed
        ? [
            el(
              'p',
              { class: 'muted small' },
              'This round is closed and its scores are final. Reopen it to correct one.',
            ),
          ]
        : [
            el(
              'p',
              { class: 'muted small' },
              'Each table saves on its own, so you can score them as you go. Nothing ' +
                'waits for the whole room.',
            ),
          ]),
      ...tables.map((t) => scoreTable(t, s, closed, act)),
      roundLockRow(s, closed, act),
      problem,
    ]);
  }

  /** The table-total check. One row, because it is one setting. */
  function scoringSettingRow(
    s: TournamentState,
    act: (p: Promise<TournamentState>, btn: HTMLElement) => void,
  ): HTMLElement {
    const box = el('input', {
      type: 'number',
      inputmode: 'numeric',
      'aria-label': 'Required table total',
      placeholder: 'none',
      value: s.tableScoreTotal === null ? '' : String(s.tableScoreTotal),
      class: 'score-total-box',
    }) as HTMLInputElement;
    const save = el('button', { class: 'btn-link', type: 'button' }, 'Set');
    save.addEventListener('click', () => {
      const raw = box.value.trim();
      const total = raw === '' ? null : Number(raw);
      if (total !== null && !Number.isInteger(total)) {
        box.focus();
        return;
      }
      act(setTournamentScoring(eventId, { tableScoreTotal: total }), save);
    });

    return el('div', { class: 'score-setting' }, [
      el('span', { class: 'muted small' }, 'Each table must total'),
      box,
      save,
      el(
        'span',
        { class: 'muted small' },
        // Named concretely, because "table total" means nothing to somebody who
        // has not read the plan. Blank is the right answer for most groups.
        'Leave blank for no check. Riichi from 25,000 each is 100000.',
      ),
    ]);
  }

  function scoreTable(
    table: TournamentRoundRow['tables'][number],
    s: TournamentState,
    closed: boolean,
    act: (p: Promise<TournamentState>, btn: HTMLElement) => void,
  ): HTMLElement {
    const boxes = new Map<number, HTMLInputElement>();
    const totalLine = el('p', { class: 'muted small score-sum' });

    const retotal = (): void => {
      let sum = 0;
      let entered = 0;
      for (const box of boxes.values()) {
        const raw = box.value.trim();
        if (raw === '') continue;
        const n = Number(raw);
        if (!Number.isFinite(n)) continue;
        sum += n;
        entered += 1;
      }
      if (entered === 0) {
        totalLine.textContent = '';
        totalLine.classList.remove('score-sum-bad', 'score-sum-ok');
        return;
      }
      if (s.tableScoreTotal === null) {
        totalLine.textContent = `Total ${sum.toLocaleString()}`;
        totalLine.classList.remove('score-sum-bad', 'score-sum-ok');
        return;
      }
      // ⚠ Only a COMPLETE table can be judged against a total. Complaining at
      // a half-typed table would make the check unusable -- the organizer is
      // mid-entry and the sum is SUPPOSED to be wrong. Mirrors the same rule in
      // `scoringProblem()`, which is the one that actually refuses a write.
      if (entered < boxes.size) {
        totalLine.textContent = `Total so far ${sum.toLocaleString()}`;
        totalLine.classList.remove('score-sum-bad', 'score-sum-ok');
        return;
      }
      const off = sum - s.tableScoreTotal;
      if (off === 0) {
        totalLine.textContent = `Total ${sum.toLocaleString()}: adds up`;
        totalLine.classList.add('score-sum-ok');
        totalLine.classList.remove('score-sum-bad');
      } else {
        // ⚠ `toLocaleString()`, NOT `formatScore()`, in both halves of this
        // line, and the reason is that they are QUANTITIES rather than deltas.
        // formatScore() always signs a positive, so the first draft printed
        // "Total +99,000 — under by +1,000": a plus on a table's total, which
        // is not a gain, and a plus on a difference whose direction the word
        // "under" has already given. `toLocaleString()` gives the separators
        // and keeps a minus where one is real, which is what a running sum in a
        // zero-sum ruleset actually needs. A PLAYER's score keeps formatScore().
        totalLine.textContent =
          `Total ${sum.toLocaleString()}: ${off > 0 ? 'over' : 'under'} by ` +
          `${Math.abs(off).toLocaleString()}`;
        totalLine.classList.add('score-sum-bad');
        totalLine.classList.remove('score-sum-ok');
      }
    };

    const rows = table.seats.map((seat) => {
      const box = el('input', {
        type: 'number',
        inputmode: 'numeric',
        'aria-label': `${seat.displayName} score`,
        value: seat.score === null ? '' : String(seat.score),
        class: 'score-box',
      }) as HTMLInputElement;
      if (closed) box.setAttribute('disabled', 'disabled');
      box.addEventListener('input', retotal);
      boxes.set(seat.playerId, box);

      return el('div', { class: 'score-row' }, [
        el('span', { class: 'muted small' }, seat.seatLabel),
        el('span', { class: 'score-name' }, seat.displayName),
        box,
      ]);
    });

    const save = el('button', { class: 'btn', type: 'button' }, 'Save table');
    save.addEventListener('click', () => {
      const scores: SeatScore[] = [];
      for (const [playerId, box] of boxes) {
        const raw = box.value.trim();
        // An empty box is "not scored yet", NOT zero. Sending 0 for it would
        // make an unscored round look complete and let it be closed.
        if (raw === '') continue;
        const n = Number(raw);
        if (!Number.isInteger(n)) {
          box.focus();
          return;
        }
        scores.push({ playerId, score: n });
      }
      if (!scores.length) return;
      act(setTournamentScores(eventId, s.currentRound, { scores }), save);
    });

    retotal();
    return el('div', { class: 'seat-table-card' }, [
      el('h3', {}, `Table ${table.tableNo}`),
      ...rows,
      totalLine,
      ...(closed ? [] : [el('div', { class: 'walkin-row' }, [save])]),
    ]);
  }

  /** Close the round, or reopen it. The line between "editable" and "final". */
  function roundLockRow(
    s: TournamentState,
    closed: boolean,
    act: (p: Promise<TournamentState>, btn: HTMLElement) => void,
  ): HTMLElement {
    if (closed) {
      const reopen = el('button', { class: 'btn-link', type: 'button' }, 'Reopen this round');
      reopen.addEventListener('click', () => {
        act(reopenTournamentRound(eventId, s.currentRound), reopen);
      });
      // Not offered at all once the tournament is over: that is what stops the
      // final standings being rewritten after a winner has been announced.
      return s.status === 'complete'
        ? el('p', { class: 'muted small' }, 'The tournament is finished, so rounds are locked.')
        : el('div', { class: 'walkin-row' }, [reopen]);
    }
    const close = el('button', { class: 'btn', type: 'button' }, `Close round ${s.currentRound}`);
    close.addEventListener('click', () => {
      act(closeTournamentRound(eventId, s.currentRound), close);
    });
    return el('div', { class: 'walkin-row' }, [
      close,
      el(
        'span',
        { class: 'muted small' },
        'Closing settles the standings for this round. It refuses while anybody is unscored.',
      ),
    ]);
  }

  // --- The draw ----------------------------------------------------------

  function drawCards(s: TournamentState): HTMLElement[] {
    if (!s.rounds.length) return [];
    // Newest round first: the current round is what the room cares about, and
    // scrolling past four played rounds to reach it would be backwards.
    return [...s.rounds].reverse().map((r) => roundCard(r, s));
  }

  function roundCard(r: TournamentRoundRow, s: TournamentState): HTMLElement {
    const isCurrent = r.roundNo === s.currentRound;
    return el('section', { class: 'card' }, [
      el('h2', {}, `Round ${r.roundNo}${isCurrent ? ' (current)' : ''}`),
      ...(r.byes.length
        ? [
            el(
              'p',
              { class: 'muted small' },
              `Sitting out: ${r.byes.map((b) => b.displayName).join(', ')}`,
            ),
          ]
        : []),
      ...(r.tables.length
        ? r.tables.map((t) =>
            el('div', { class: 'draw-table' }, [
              el('h3', {}, `Table ${t.tableNo}`),
              el(
                'ul',
                {},
                t.seats.map((seat) =>
                  el('li', {}, [
                    el('span', { class: 'muted small' }, `${seat.seatLabel}: `),
                    el('span', {}, seat.displayName),
                  ]),
                ),
              ),
            ]),
          )
        : [el('p', { class: 'muted' }, 'Not seated yet.')]),
    ]);
  }

  await load();
};

/**
 * What to tell a manager a frame meant.
 *
 * Worded from the reason and round the frame carries, which are the only two
 * things it may carry: both are public to a tournament room, and neither is
 * per-viewer. Anything more specific would need a fetch, which is the thing
 * this notice exists to avoid doing behind the organizer's back.
 */
function describeChange(reason: TournamentChangeReason, roundNo: number): string {
  switch (reason) {
    case 'round':
      return `Round ${roundNo} was opened by somebody else. Every player's table is blank until it is drawn.`;
    case 'seated':
      return `Round ${roundNo} was seated by somebody else.`;
    case 'scored':
      return `Round ${roundNo}'s scores were changed by somebody else. The standings have moved.`;
    case 'roster':
      return 'The roster was changed by somebody else.';
    case 'complete':
      return 'The tournament was ended by somebody else.';
    default: {
      const unhandled: never = reason;
      return unhandled;
    }
  }
}

/**
 * A score, with its sign and its thousands separators.
 *
 * ⚠ The PLUS is not decoration. Riichi and MCR scores are signed and a round
 * routinely produces "+12300" and "-4100"; showing the positive one bare makes
 * a column of numbers where only the losers are marked, and the reader has to
 * work out that the unmarked ones are gains. Zero gets no sign, because "+0"
 * reads as a rounding artefact.
 *
 * `toLocaleString` handles the separators, so a 100,000-point riichi total is
 * legible at a glance in a room rather than a run of six digits.
 */
function formatScore(score: number): string {
  const body = Math.abs(score).toLocaleString();
  if (score > 0) return `+${body}`;
  if (score < 0) return `-${body}`;
  return '0';
}

function backLink(eventId: number): HTMLElement {
  return el('p', {}, [el('a', { href: `/events/${eventId}` }, 'Back to the event')]);
}

function statusLine(s: TournamentState): string {
  const active = s.players.filter((p) => p.status === 'active').length;
  if (s.status === 'complete') return `Finished · ${active} players`;
  if (s.status === 'setup') return `Not started · ${active} players`;
  return `Round ${s.currentRound} · ${active} players`;
}

/** "6 tables of 4, and 1 person sits out" — the shape of a DRAW, before drawing it. */
function drawPlanLine(shape: ReturnType<typeof drawShape>, seatsPerTable: number): string {
  if (shape.tables === 0) {
    return `Not enough players for a table of ${seatsPerTable} yet.`;
  }
  const base = `${shape.tables} ${shape.tables === 1 ? 'table' : 'tables'} of ${seatsPerTable}`;
  if (shape.byes === 0) return `${base}, with everybody playing.`;
  // ⚠ The draw sits the remainder OUT rather than running a short table, which
  // is the opposite of what the hand editor offers below — mahjong is
  // four-handed, so the app will not decide to pad a table on the organizer's
  // behalf. Saying which people, and that it rotates, is what stops this
  // reading as the app losing somebody.
  const who = shape.byes === 1 ? '1 person sits' : `${shape.byes} people sit`;
  return `${base}, and ${who} out: whoever has sat out least.`;
}

/** "Drawn: 6 tables, 1 sitting out, no repeat pairings." */
function drawResultLine(result: TournamentDrawResult): string {
  const bits = [`${result.byePlayerIds.length ? `${result.byePlayerIds.length} sitting out` : 'everybody playing'}`];
  bits.push(
    result.repeatPairs === 0
      ? `nobody is playing an old opponent (${result.seatedPairs} pairings)`
      : // ⚠ The VERB has to agree, and the singular case only appears at one
        // particular roster size -- "1 of 18 pairings have played before" is
        // what this printed first. Exactly the bug `tablePlanLine()` already
        // carries a note about, in the same file, made again.
        `${result.repeatPairs} of ${result.seatedPairs} ${
          result.repeatPairs === 1 ? 'pairings has' : 'pairings have'
        } played before`,
  );
  // Re-draw is offered in the same breath, because the number is only useful if
  // there is something to do about it.
  return `Drawn: ${bits.join(', ')}. Re-draw for a different arrangement.`;
}

/**
 * "25 tables of 4" — and the remainder said out loud when there is one.
 *
 * The remainder is the honest part: mahjong is a four-handed game, so a table
 * of three is a real decision for the organizer rather than an arithmetic
 * detail. Surfaced at setup, where they can still go and find a fourth.
 */
function tablePlanLine(
  plan: ReturnType<typeof tablesNeeded>,
  seatsPerTable: number,
): string {
  if (plan.tables === 0) return 'Nobody on the roster yet.';
  const base = `${plan.tables} ${plan.tables === 1 ? 'table' : 'tables'} of ${seatsPerTable}`;
  if (plan.remainder === 0) return `${base}, exactly.`;
  // The VERB has to agree too: "1 seat sit empty" was the first thing this
  // printed. Cheap to get wrong because the singular case only appears at one
  // specific roster size.
  const seats = plan.emptySeats === 1 ? '1 seat sits' : `${plan.emptySeats} seats sit`;
  return `${base}. The last one has ${plan.remainder} of ${seatsPerTable}, so ${seats} empty.`;
}
